import { MessageFilled } from "@ant-design/icons";
import { Flex } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Point,
    TaskPromoTagPhotos,
    TaskRoute,
    TaskStatus,
} from "tm-shared/types";
import { uniqueByKey } from "tm-shared/utils";

import { GalleryUploader, Viewfinder } from "@/domains/media";
import { usePointDistance } from "@/domains/point";
import { useCatalog } from "@/domains/product";
import {
    CommentModal,
    StatusCycleButton,
    TaskInfoModal,
    useActiveScrollNavigator,
    useCheckCommentOnReject,
    useCompleteTask,
    useStickyFilteredItems,
    useTaskCompletion,
    useTaskConfigSelector,
} from "@/domains/tasks";
import { SVScanButton } from "@/features/SupervisorScan";
import { useAsyncAction } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useTaskPayloadSelector,
} from "@/hooks/redux";
import {
    makeCommand,
    useMakeCommand,
} from "@/hooks/requestQueue/useRequestQueue";
import { TaskFinishButton } from "@/pages/WorkPlan/Tasks/components/TaskFinishButton";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { BottomSheet, OutlineIconButton, PrimaryText } from "@/shared/ui";
import {
    mutatePromoTagPhotosPhotos,
    patchPromoTagPhotosEntity,
} from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { buildPhotoCaptions } from "@/utils/photo/captions";
import { getPhotoIdsFromPhotoTask } from "@/utils/photo/getPhotoIdsFromPhotoTask";

import { CameraPreviewOverlay } from "./components/CameraPreviewOverlay";
import { PromoTagPhotosController } from "./components/PromoTagPhotosController";
import { TaskPromoTag } from "./components/TaskPromoTag";
import {
    StyledBottomSheetTop,
    StyledList,
    StyledMainContainer,
} from "./Styled";

interface PromoTagPhotosPageProps {
    point: Point;
    task: TaskPromoTagPhotos;
}

export const PromoTagPhotosPage: FC<PromoTagPhotosPageProps> = ({
    task,
    point,
}) => {
    const dispatch = useAppDispatch();
    const [modal, setModal] = useState<"comment" | "taskInfo" | null>(null);

    const pointId = point.id;
    const taskId = task.id;

    const { products: catalogProducts } = useCatalog();

    const firstName = useAppSelector((s) => s.user.firstName);
    const lastName = useAppSelector((s) => s.user.lastName);

    const { activeItem, showUncompleted } = useTaskConfigSelector({
        pointId,
        taskId,
    });
    const safeActiveItem = activeItem ?? "";

    const productPayloadsMap = useTaskPayloadSelector(
        TaskRoute.PromoTagPhotos,
        taskId,
    );
    const activePayload = productPayloadsMap?.[safeActiveItem];
    const activeStatus = activePayload?.status ?? TaskStatus.Incomplete;

    const taskProducts = useMemo(
        () => uniqueByKey(task.context.products, "barcode"),
        [task.context.products],
    );

    const prevBarcodeRef = useRef<string | null>(null);

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                value: null,
            }),
        );
    }, [dispatch, pointId, taskId]);

    const handlerMessage = useCallback(() => {
        if (activeStatus === TaskStatus.Incomplete || !activeStatus) return;

        setModal("comment");
    }, [activeStatus]);

    const handleChangeComment = useCallback(
        (newComment: string) => {
            if (!activeItem) return;

            dispatch(
                patchPromoTagPhotosEntity({
                    taskId,
                    barcode: activeItem,
                    patch: { comment: newComment },
                }),
            );
        },
        [activeItem, dispatch, taskId],
    );

    const onClickInfoButton = () => {
        setModal("taskInfo");
    };

    const resetStatusIfNeeded = useCallback(
        (barcode: string) => {
            const payload = productPayloadsMap?.[barcode];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchPromoTagPhotosEntity({
                        taskId,
                        barcode,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [productPayloadsMap, dispatch, taskId],
    );

    const onChangeActive = useCallback(
        (barcode: string | null) => {
            dispatch(
                setMdTasksConfig({
                    pointId,
                    taskId,
                    value: barcode,
                }),
            );
        },
        [dispatch, pointId, taskId],
    );

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, barcode?: string) => {
            const targetBarcode = barcode ?? activeItem;

            if (!targetBarcode) return;

            dispatch(
                patchPromoTagPhotosEntity({
                    taskId,
                    barcode: targetBarcode,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [activeItem, dispatch, taskId],
    );

    const onChangePerformerValue = useCallback(
        (value: number | null, barcode: string) => {
            dispatch(
                patchPromoTagPhotosEntity({
                    taskId,
                    barcode,
                    patch: { performerValue: value },
                }),
            );
        },
        [dispatch, taskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activeItem) return;

            dispatch(
                mutatePromoTagPhotosPhotos({
                    taskId,
                    barcode: activeItem,
                    option: "add",
                    photo,
                }),
            );
        },
        [activeItem, dispatch, taskId],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activeItem) return;

            dispatch(
                mutatePromoTagPhotosPhotos({
                    taskId,
                    barcode: activeItem,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [activeItem, dispatch, taskId],
    );

    const { validateAndNotify } = useCheckCommentOnReject();

    const handleCheckCommentOnReject = useCallback(
        (barcode: string | null) => {
            const prevDmpId = prevBarcodeRef.current ?? "";
            const payload = productPayloadsMap?.[prevDmpId];

            if (validateAndNotify(payload)) {
                return;
            }

            onChangeActive(barcode);
        },
        [productPayloadsMap, onChangeActive, validateAndNotify],
    );

    const { filteredItems: filteredTasks } = useStickyFilteredItems({
        items: taskProducts,
        showUncompleted: showUncompleted ?? false,
        getItemId: (task) => task.barcode,
        payloadsMap: productPayloadsMap ?? {},
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    const { getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredTasks.map((t) => t.barcode),
            activeId: safeActiveItem,
            onChangeActive: handleCheckCommentOnReject,
        });

    const activeProductName = catalogProducts[safeActiveItem]?.name ?? "";

    useEffect(() => {
        const prev = prevBarcodeRef.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevBarcodeRef.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();
    const completeTask = useCompleteTask({ targetId: pointId, taskId });

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        if (!productPayloadsMap) return;

        const photoIds = getPhotoIdsFromPhotoTask(productPayloadsMap);
        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload: productPayloadsMap },
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),
            ...taskCompleteCommands,
            makeCommand({
                type: "updatePhotoStorage",
                title: "Синхронізація сховища",
                params: photoIds,
                options: {
                    stopOnError: false,
                },
            }),
        ];

        completeTask({ commands });
    });

    const { isAllCompleted } = useTaskCompletion({
        payloadMap: productPayloadsMap,
        expectedCount: taskProducts.length,
        isPayloadComplete: (payload) => {
            if (payload.status === TaskStatus.Completed) {
                return payload.performerValue !== null;
            }

            return true;
        },
    });

    const { distance } = usePointDistance({
        pointGeo: point.market.building.geo,
    });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                pointName: point.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, point.name, task.name],
    );

    useEffect(() => {
        scrollToActive();
    }, [isAllCompleted, scrollToActive]);

    return (
        <>
            <Header
                back
                title={task.name}
                rightActions={
                    <>
                        <SVScanButton pointId={pointId} taskId={taskId} />
                    </>
                }
            />

            <PromoTagPhotosController point={point} task={task} />

            <StyledMainContainer>
                <StyledList style={{ overflowY: "auto" }}>
                    {filteredTasks.map((product) => {
                        const { barcode } = product;
                        const currentDMPPayload = productPayloadsMap?.[barcode];
                        const isActive = barcode === activeItem;

                        return (
                            <div key={barcode} ref={getItemRef(barcode)}>
                                <TaskPromoTag
                                    product={product}
                                    catalogProduct={catalogProducts[barcode]}
                                    isActive={isActive}
                                    onChangeActive={onChangeActive}
                                    payload={currentDMPPayload}
                                    onChangeTaskStatus={onChangeTaskStatus}
                                    onChangePerformerValue={
                                        onChangePerformerValue
                                    }
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <TaskFinishButton
                    onClick={handleComplete}
                    visible={isAllCompleted}
                    padding="8px 16px"
                    loading={completeLoading}
                />
            </StyledMainContainer>

            <BottomSheet
                open={!!activeItem}
                onClose={onCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={onClickInfoButton}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>{activeProductName}</PrimaryText>
                    <Flex gap="20px" justify="center" align="center">
                        <OutlineIconButton
                            icon={<MessageFilled />}
                            onClick={handlerMessage}
                            disabled={activeStatus === TaskStatus.Incomplete}
                        />
                        <StatusCycleButton
                            value={activeStatus}
                            onChange={onChangeTaskStatus}
                            states={[
                                TaskStatus.Incomplete,
                                TaskStatus.Rejected,
                            ]}
                            disabled={activeStatus === TaskStatus.Completed}
                        />
                    </Flex>
                </StyledBottomSheetTop>

                <GalleryUploader
                    previewTitle={safeActiveItem}
                    onDelete={onDeletePhoto}
                    onUpload={onUploadPhoto}
                    activeItemId={safeActiveItem}
                    disabled={activeStatus === TaskStatus.Rejected}
                    pointId={pointId}
                    savedPhotos={activePayload?.photos ?? []}
                    captionLines={captions}
                    cameraProps={{
                        viewfinder: Viewfinder,
                        previewOverlay: activeItem ? (
                            <CameraPreviewOverlay barcode={activeItem} />
                        ) : undefined,
                    }}
                />
            </BottomSheet>

            {modal === "comment" && activeItem && (
                <CommentModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activePayload?.comment ?? ""}
                    taskStatus={activeStatus}
                />
            )}

            {modal === "taskInfo" && (
                <TaskInfoModal
                    onCloseModal={() => setModal(null)}
                    title={"Фото акційних цінників"}
                    description={
                        "Зробіть фото акційних цінників для кожного товару. На фото має бути видно товар і його акційну ціну. Якщо фото неможливо зробити — виберіть статус і додайте коментар."
                    }
                />
            )}

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
